import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'five-source-types', title: '1. The five source types' },
  { id: 'documents', title: '2. Documents' },
  { id: 'links', title: '3. Links & crawling' },
  { id: 'text-qna-audio', title: '4. Text, Q&A and audio' },
  { id: 'limits', title: '5. Limits' },
  { id: 'organizing', title: '6. Organising knowledge' },
  { id: 'best-practices', title: '7. What makes retrieval good' },
];

const KnowledgeUploadPage = () => {
  return (
    <>
      <NextSeo
        title="The Knowledge Upload Playbook"
        description="Which knowledge source type to use for which content, what Chat.co accepts, the real plan limits, and the content decisions that determine whether retrieval works."
        canonical="https://chat.co/docs/knowledge-upload"
        openGraph={{
          url: 'https://chat.co/docs/knowledge-upload',
          title: 'The Knowledge Upload Playbook | Chat.co',
          description:
            'Which knowledge source type to use for which content, what Chat.co accepts, and the content decisions that determine whether retrieval works.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Getting Started',
            tags: ['knowledge base', 'document upload', 'website crawling', 'AI training'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Knowledge Upload Guide',
            },
          ],
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Getting Started"
        categoryColor="text-green-700"
        title="The knowledge upload playbook"
        description="Five source types, one knowledge base. Which to reach for, what Chat.co will and won't accept, and the content decisions that decide whether retrieval works at all."
        readingTime="8 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Your first AI agent',
          href: PageLink.docsGettingStarted,
        }}
        nextArticle={{
          title: 'Tuning your bot',
          href: PageLink.docsTuneBot,
        }}
        keyTakeaways={[
          'Documents, Links, Text, Q&A and Audio are five separate screens under Knowledge Base',
          'Uploads accept PDF, DOC, DOCX, XLS, XLSX, CSV and Markdown — not plain .txt',
          'A crawl pauses so you can review discovered pages before anything is indexed',
          'Q&A pairs are the lever for pinning an exact answer wording',
          'Knowledge base size is metered in characters, not files or megabytes',
        ]}>
        <p className="lead">
          Your agent is exactly as good as what it can retrieve. Most disappointing bots are not
          model problems — they are knowledge problems: the right document was never uploaded, or it
          was uploaded in a shape that retrieval cannot use. This is the playbook for getting that
          part right.
        </p>

        <section id="five-source-types" className="scroll-mt-28">
          <h2>1. The five source types</h2>
          <p>
            Open a bot and look at the <strong>Knowledge Base</strong> group in the sidebar. Each
            entry is a genuinely different ingestion path, not five doors to the same box.
          </p>

          <DocsTable
            headers={['Source', 'Use it for', 'Avoid it for']}
            rows={[
              [
                <strong key="d">Documents</strong>,
                'Files you already have — manuals, contracts, reports, spreadsheets',
                'Content that changes weekly; you would be re-uploading constantly',
              ],
              [
                <strong key="l">Links</strong>,
                'Anything already published on the web — help centres, docs sites, blogs',
                'Pages behind a login, or JavaScript apps that render nothing server-side',
              ],
              [
                <strong key="t">Text</strong>,
                'Short policies and facts that live in someone’s head, not in a file',
                'Anything long — a 40-page policy belongs in Documents',
              ],
              [
                <strong key="q">Q&amp;A</strong>,
                'Questions where the answer must be worded exactly this way',
                'General background; Q&A is a scalpel, not a corpus',
              ],
              [
                <strong key="a">Audio</strong>,
                'Recorded training, webinars, briefings — transcribed on upload',
                'Anything you also have as a document; the document is cleaner',
              ],
            ]}
          />

          <DocsCallout variant="tip" title="Mix them">
            The strongest knowledge bases use three or four of these. Crawl the public site for
            breadth, upload the internal PDFs for depth, and add a dozen Q&amp;A pairs for the
            questions where the wording matters — pricing, refunds, anything legal.
          </DocsCallout>
        </section>

        <section id="documents" className="scroll-mt-28">
          <h2>2. Documents</h2>

          <h3>What is accepted</h3>
          <p>
            <strong>Knowledge Base → Documents</strong> accepts these file types, up to ten files
            per batch:
          </p>

          <DocsCardGrid columns={4}>
            <DocsCard icon="tabler:file-type-pdf" title="PDF">
              <code>.pdf</code>
            </DocsCard>
            <DocsCard icon="tabler:file-type-docx" title="Word">
              <code>.doc</code> · <code>.docx</code>
            </DocsCard>
            <DocsCard icon="tabler:file-type-xls" title="Excel">
              <code>.xls</code> · <code>.xlsx</code>
            </DocsCard>
            <DocsCard icon="tabler:file-type-csv" title="CSV &amp; Markdown">
              <code>.csv</code> · <code>.md</code>
            </DocsCard>
          </DocsCardGrid>

          <DocsCallout variant="warning" title="There is no .txt upload">
            Plain text files are rejected by the dropzone. Paste that content into{' '}
            <strong>Knowledge Base → Text</strong> instead, or rename the file to <code>.md</code>{' '}
            and upload it as Markdown — the extraction path is the same.
          </DocsCallout>

          <h3>Uploading</h3>
          <DocsSteps>
            <DocsStep title="Open Knowledge Base → Documents">
              <p>Select the bot first — knowledge is scoped per bot, never shared implicitly.</p>
            </DocsStep>
            <DocsStep title="Drop files onto the dropzone">
              <p>
                Up to ten at a time. Files over your plan&apos;s per-file ceiling are rejected
                before upload starts rather than failing halfway.
              </p>
            </DocsStep>
            <DocsStep title="Let each row finish">
              <p>
                Text extraction, chunking and indexing happen per file. A short document is done in
                seconds; a long scanned PDF takes minutes because the pages have to be read
                optically first.
              </p>
            </DocsStep>
            <DocsStep title="Spot-check in Test Bot">
              <p>
                Ask a question whose answer only exists in the file you just added. If it comes back
                cited, that file is genuinely in the index.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCallout variant="note" title="Text-based PDFs beat scans, every time">
            A PDF exported from a word processor carries a real text layer. A scanned or
            photographed one carries pixels, and has to go through character recognition first —
            which is slower and introduces errors that then propagate into answers. If you have the
            original, upload the original.
          </DocsCallout>
        </section>

        <section id="links" className="scroll-mt-28">
          <h2>3. Links &amp; crawling</h2>
          <p>
            <strong>Knowledge Base → Links</strong> pulls content straight off the web. Four modes
            are available:
          </p>

          <DocsTable
            headers={['Mode', 'What it does']}
            rows={[
              [
                <strong key="s">Smart</strong>,
                'Discovers the pages that look like real content and skips the navigational chaff. The right default for most sites.',
              ],
              [
                <strong key="f">Full website</strong>,
                'Follows links recursively from the URL you give it. Thorough, and correspondingly noisy on a large site.',
              ],
              [
                <strong key="p">Single page</strong>,
                'Exactly one URL. Good for a specific article, a pricing page, or a policy.',
              ],
              [
                <strong key="y">YouTube</strong>,
                'Pulls the transcript from a video. Useful for recorded training and product walkthroughs.',
              ],
            ]}
          />

          <h3>You review before anything is indexed</h3>
          <p>
            A crawl does not silently absorb whatever it finds. It discovers pages, then pauses and
            shows you the list. You select which pages to keep, and only those are stored and
            indexed. That review step is the single best defence against a knowledge base polluted
            with cookie banners, tag archives and paginated listing pages.
          </p>

          <DocsCallout variant="tip" title="Deselect aggressively on the first crawl">
            Category listings, author pages, tag archives and paginated indexes add tokens and
            subtract precision — they contain fragments of many topics and are strong matches for
            almost nothing. Keep the pages a human would actually send someone.
          </DocsCallout>

          <h3>Keeping crawled content fresh</h3>
          <p>
            Crawls are point-in-time. When a source page changes, re-run the crawl to pick it up.
            Automated recurring source refreshes exist on the platform but are currently limited to
            managed listings rather than being available on every account — talk to us if you need a
            standing refresh schedule for a bot.
          </p>
        </section>

        <section id="text-qna-audio" className="scroll-mt-28">
          <h2>4. Text, Q&amp;A and audio</h2>

          <h3>Text</h3>
          <p>
            A single free-form body of text per bot. This is the right home for the things nobody
            ever wrote down properly: the escalation rule, the seasonal exception, the caveat
            everyone knows but no document states.
          </p>

          <h3>Q&amp;A pairs</h3>
          <p>
            An explicit question and its exact answer. Reach for these when the wording is not
            negotiable — pricing, refund terms, anything with legal or contractual weight, and
            anything the model keeps getting subtly wrong no matter how good the source document is.
          </p>

          <DocsCallout variant="tip" title="Let the bot tell you which pairs to write">
            Don&apos;t guess. <Link href={PageLink.docsAnalyticsRoi}>Knowledge Gaps</Link> surfaces
            the questions your bot actually failed to answer and can turn one straight into a
            Q&amp;A pair. That loop is worth more than an afternoon of brainstorming.
          </DocsCallout>

          <h3>Audio</h3>
          <p>
            Upload a recording and Chat.co transcribes it into searchable knowledge. Both the total
            minutes and the number of audio files per bot are metered by plan. Transcripts of
            unstructured conversation retrieve less cleanly than written documents, so if the same
            material exists as a deck or a document, prefer that.
          </p>
        </section>

        <section id="limits" className="scroll-mt-28">
          <h2>5. Limits</h2>
          <p>
            Knowledge base capacity is metered in <strong>characters of extracted text</strong>, not
            in files or megabytes. A 40&nbsp;MB image-heavy PDF may contribute less than a 2&nbsp;MB
            text one.
          </p>

          <DocsTable
            headers={['Plan', 'Knowledge base', 'Max file size', 'URL crawls', 'Audio minutes']}
            rows={[
              ['Starter', '2M characters', '50 MB', '500', '30'],
              ['Pro', '5M characters', '100 MB', '10,000', '120'],
              ['Business', '25M characters', '500 MB', '25,000', '600'],
              ['Enterprise', 'Unlimited', '5 GB', 'Unlimited', 'Unlimited'],
            ]}
          />

          <DocsCallout variant="note" title="The pricing page is authoritative">
            Plan contents change. The table above reflects the current definitions, but if you are
            making a purchasing decision, check{' '}
            <Link href={PageLink.pricing}>the pricing page</Link> — and note that knowledge base,
            message credits, extra bots and team seats are all separately expandable as add-ons
            without changing plan.
          </DocsCallout>

          <p>
            Two million characters is roughly a thousand pages of ordinary prose. Most bots that
            struggle are nowhere near a capacity limit — they are struggling on content quality.
          </p>
        </section>

        <section id="organizing" className="scroll-mt-28">
          <h2>6. Organising knowledge</h2>
          <p>
            Knowledge lives per bot. There are no folders inside a bot&apos;s knowledge base, and
            that is a deliberate constraint: the unit of separation is the bot itself.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:robot" title="One bot">
              Everything relates to one audience answering one kind of question. A product support
              bot over product docs. Simplest to run and to reason about.
            </DocsCard>
            <DocsCard icon="tabler:robots" title="Several bots">
              Audiences genuinely differ — a customer-facing bot and an internal-staff bot should
              never share a corpus, because the internal one contains things customers must not see.
            </DocsCard>
          </DocsCardGrid>

          <p>
            The decisive question is not &ldquo;is this a lot of content?&rdquo; but{' '}
            <strong>
              &ldquo;would I be comfortable if any of this reached any of these readers?&rdquo;
            </strong>{' '}
            If the answer is no, that is a second bot, not a second folder.{' '}
            <Link href={PageLink.docsMultiBotOrchestration}>Managing multiple bots and teams</Link>{' '}
            covers the operational side.
          </p>
        </section>

        <section id="best-practices" className="scroll-mt-28">
          <h2>7. What makes retrieval good</h2>

          <h3>Structure beats volume</h3>
          <p>
            Retrieval works on passages. A document with real headings, short paragraphs and one
            idea per section produces passages that stand on their own. A wall of undifferentiated
            text produces passages that make no sense out of context — and out of context is exactly
            where they end up.
          </p>

          <h3>Say the thing the way people ask it</h3>
          <p>
            Your customers type &ldquo;can I get my money back&rdquo;, not &ldquo;refund eligibility
            criteria&rdquo;. If your content only ever uses the formal phrasing, add the colloquial
            one somewhere — a Q&amp;A pair is the cheapest place to do it.
          </p>

          <h3>Delete contradictions</h3>
          <p>
            Two documents stating different return windows is worse than having neither, because
            retrieval will surface one of them unpredictably. When you supersede a policy, remove
            the old source rather than leaving both in place.
          </p>

          <h3>Re-test after every significant change</h3>
          <p>
            Keep a short list of questions you know the answers to and run them through{' '}
            <strong>Test Bot</strong> after each meaningful knowledge change. It takes two minutes
            and catches the case where a new upload quietly outranks the source that was answering
            correctly.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:quote"
              title="Citation quality"
              href={PageLink.docsCitationQuality}>
              When answers are right but citations are wrong, or vice versa.
            </DocsCard>
            <DocsCard
              icon="tabler:tool"
              title="Troubleshooting"
              href={PageLink.docsTroubleshooting}>
              Failed uploads, empty answers, and crawls that find nothing.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default KnowledgeUploadPage;
